import React, { useEffect, useState } from "react";
import {
    fetchAdminTopTokens,
    updateAdminTopTokens,
    fetchSocials,
    updateSocials,
    fetchSaleStatus,
} from "../lib/api";
import {
    fetchPhase,
    setPhase as setPhaseApi,
    fetchRewards,
    createReward,
    deleteReward,
    adminMe,
    adminLogout,
    getAdminToken,
    clearAdminToken,
} from "../lib/admin";
import AdminLogin from "../components/AdminLogin";
import { SOCIAL_DEFS } from "../components/Socials";
import { toast } from "sonner";
import { Trash2, Plus, Power, LogOut } from "lucide-react";

const RARITY_OPTIONS = [
    { v: "ultra_legendary", l: "Ultra Legendary" },
    { v: "legendary", l: "Legendary" },
    { v: "mythic", l: "Mythic" },
    { v: "epic", l: "Epic" },
];

const RELEASE_OPTIONS = [
    { v: "early_access", l: "Early Access" },
    { v: "public_sale", l: "Public Sale" },
    { v: "manual", l: "Manual (admin only)" },
];

const PHASES = [
    { v: "closed", l: "Closed", desc: "All sales closed. No mint, no bid.", color: "border-slate-500 text-slate-400" },
    { v: "early_access", l: "Early Access", desc: "2,000 normals @ $25 + 4 top auctions live.", color: "border-frost text-frost" },
    { v: "public_sale", l: "Public Sale", desc: "12,187 normals @ $80 + 6 top auctions live.", color: "border-aurora text-aurora" },
    { v: "ended", l: "Ended", desc: "Mint closed. Manual auction settlement only.", color: "border-pink-500 text-pink-400" },
];

export default function AdminPage() {
    const [authed, setAuthed] = useState(null); // null = checking, false = needs login, true = authed
    const [tops, setTops] = useState([]);
    const [phaseState, setPhaseState] = useState("closed");
    const [socials, setSocialsState] = useState({});
    const [status, setStatus] = useState(null);
    const [rewards, setRewards] = useState([]);
    const [newReward, setNewReward] = useState({ title: "", description: "", image_url: "", recipient: "" });
    const [busy, setBusy] = useState(false);

    const loadAll = async () => {
        // Skip the network round-trip if we have no token at all.
        if (!getAdminToken()) {
            setAuthed(false);
            return;
        }
        try {
            await adminMe();
        } catch (err) {
            console.error("adminMe failed:", err?.response?.status);
            clearAdminToken();
            setAuthed(false);
            return;
        }
        // Mark authed immediately so the dashboard renders even if a non-critical
        // fetch later fails. Each section then re-renders as its data arrives.
        setAuthed(true);
        try {
            const [t, p, s, st, r] = await Promise.all([
                fetchAdminTopTokens(),
                fetchPhase(),
                fetchSocials(),
                fetchSaleStatus(),
                fetchRewards(),
            ]);
            setTops(t.top_tokens);
            setPhaseState(p.phase_state);
            setSocialsState(s);
            setStatus(st);
            setRewards(r);
        } catch (err) {
            console.error("loadAll fetch failed:", err);
            toast.error("Some admin data failed to load");
        }
    };
    useEffect(() => { loadAll(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

    if (authed === null) {
        return (
            <div data-testid="admin-loading" className="max-w-6xl mx-auto px-4 md:px-8 py-24 text-center text-slate-500 font-mono text-sm">
                Checking session…
            </div>
        );
    }
    if (!authed) return <AdminLogin onLogin={() => loadAll()} />;

    const handleLogout = async () => {
        try {
            await adminLogout();
        } catch (err) {
            console.error("Logout failed:", err);
        }
        clearAdminToken();
        setAuthed(false);
        toast.success("Logged out");
    };

    const updateTopField = (i, key, val) => {
        const next = [...tops];
        next[i] = { ...next[i], [key]: val };
        setTops(next);
    };

    const saveTops = async () => {
        setBusy(true);
        try {
            const payload = tops.map((t) => ({
                number: parseInt(t.number, 10),
                title: t.title,
                rarity: t.rarity,
                base_price: parseFloat(t.base_price),
                release_phase: t.release_phase,
                is_open: !!t.is_open,
                custom_lat: parseFloat(t.custom_lat),
                custom_lon: parseFloat(t.custom_lon),
                quote: t.quote || "",
                description: t.description || "",
                quote_it: t.quote_it || "",
                description_it: t.description_it || "",
                title_it: t.title_it || "",
                custom_image_url: t.custom_image_url || "",
                auction_end: t.auction_end || null,
            }));
            await updateAdminTopTokens(payload);
            toast.success("Top tokens saved");
            loadAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const changePhase = async (v) => {
        try {
            await setPhaseApi(v);
            setPhaseState(v);
            toast.success(`Phase set to ${v.replace("_", " ")}`);
            loadAll();
        } catch (e) {
            console.error("changePhase failed:", e);
            toast.error("Failed to change phase");
        }
    };

    const saveSocials = async () => {
        try {
            await updateSocials(socials);
            toast.success("Socials saved");
        } catch (e) {
            console.error("saveSocials failed:", e);
            toast.error("Failed");
        }
    };

    const addReward = async () => {
        if (!newReward.title) return toast.error("Title required");
        try {
            const r = await createReward(newReward);
            setRewards([r, ...rewards]);
            setNewReward({ title: "", description: "", image_url: "", recipient: "" });
            toast.success("Reward token created");
        } catch (e) {
            console.error("addReward failed:", e);
            toast.error("Failed to create reward");
        }
    };

    const removeReward = async (id) => {
        try {
            await deleteReward(id);
            setRewards(rewards.filter((r) => r.id !== id));
            toast.success("Deleted");
        } catch (e) {
            console.error("removeReward failed:", e);
            toast.error("Failed");
        }
    };

    return (
        <div data-testid="admin-page" className="max-w-6xl mx-auto px-4 md:px-8 py-12">
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-[0.3em] text-gold">Admin</div>
                <button
                    data-testid="admin-logout"
                    onClick={handleLogout}
                    className="btn-sharp inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-[10px] hover:border-red-400/40 hover:text-red-400"
                >
                    <LogOut className="w-3 h-3" /> Logout
                </button>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight">
                Mission <span className="italic">control</span>
            </h1>

            {/* TREASURY */}
            <Section title="Treasury · Auto-withdraw" subtitle="Funds flow directly to this wallet at every mint and auction settlement. No backend custody — withdrawal is instant by design.">
                <div className="bg-slate-900/40 border border-white/10 p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Field2 label="Receiver wallet">
                        <code className="text-frost text-sm break-all">
                            {status?.receiver_wallet}
                        </code>
                    </Field2>
                    <Field2 label="Network">
                        <span className="text-white">{status?.chain_name} (chain {status?.chain_id})</span>
                    </Field2>
                    <Field2 label="Currency">
                        <span className="text-white">USDC</span>
                        <code className="text-[11px] text-slate-500 break-all block mt-1">{status?.usdc_address}</code>
                    </Field2>
                </div>
                <p className="text-[11px] font-mono text-slate-500 mt-2">
                    To change the wallet edit RECEIVER_WALLET in /app/backend/.env and REACT_APP_RECEIVER_WALLET in /app/frontend/.env, then restart services.
                </p>
            </Section>

            {/* PHASE */}
            <Section title="Sale phase" subtitle="Open / close early access and switch to public sale.">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    {PHASES.map((p) => (
                        <button
                            key={p.v}
                            data-testid={`admin-phase-${p.v}`}
                            onClick={() => changePhase(p.v)}
                            className={`text-left p-4 border bg-slate-900/40 transition-all hover:bg-slate-900 ${
                                phaseState === p.v ? p.color + " ring-1" : "border-white/10 text-slate-300"
                            }`}
                        >
                            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em]">
                                <Power className="w-3 h-3" /> {p.l}
                            </div>
                            <p className="text-[11px] font-mono text-slate-400 mt-2 leading-relaxed">{p.desc}</p>
                            {phaseState === p.v && (
                                <div className="mt-2 text-[10px] uppercase tracking-widest">● Active</div>
                            )}
                        </button>
                    ))}
                </div>
            </Section>

            {/* TOP TOKENS */}
            <Section title="13 Top tokens" subtitle="Edit title, rarity, base price, real coordinates, story and custom image (URL). Mark as open to release manual auctions.">
                <div className="space-y-3">
                    {tops.map((t, i) => (
                        <details
                            key={t.number ?? i}
                            className="bg-slate-900/40 border border-white/10 group"
                            open={i === 0}
                        >
                            <summary className="cursor-pointer p-4 flex items-center justify-between list-none">
                                <div className="flex items-center gap-4">
                                    <span className="font-serif text-2xl text-aurora w-8 text-center">{i + 1}</span>
                                    <div>
                                        <div className="font-serif text-xl">{t.title || "Untitled"}</div>
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                            #{t.number} · {(t.rarity || "").replace("_", " ")} · {(t.base_price || 0).toLocaleString()} USDC · rel: {t.release_phase}
                                            {t.release_phase === "manual" && (
                                                <span className={`ml-2 ${t.is_open ? "text-aurora" : "text-slate-500"}`}>
                                                    {t.is_open ? "● OPEN" : "○ CLOSED"}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <span className="text-slate-400 text-xs">edit ▾</span>
                            </summary>

                            <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-12 gap-3">
                                <FieldA label="Title" cols="md:col-span-4">
                                    <input
                                        data-testid={`admin-title-${i}`}
                                        value={t.title || ""}
                                        onChange={(e) => updateTopField(i, "title", e.target.value)}
                                        className={inputCls}
                                    />
                                </FieldA>
                                <FieldA label="Rarity" cols="md:col-span-3">
                                    <select
                                        data-testid={`admin-rarity-${i}`}
                                        value={t.rarity || "epic"}
                                        onChange={(e) => updateTopField(i, "rarity", e.target.value)}
                                        className={inputCls}
                                    >
                                        {RARITY_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                                    </select>
                                </FieldA>
                                <FieldA label="Base price (USDC)" cols="md:col-span-3">
                                    <input
                                        data-testid={`admin-price-${i}`}
                                        type="number"
                                        value={t.base_price}
                                        onChange={(e) => updateTopField(i, "base_price", e.target.value)}
                                        className={inputCls}
                                    />
                                </FieldA>
                                <FieldA label="Token #" cols="md:col-span-2">
                                    <input
                                        data-testid={`admin-number-${i}`}
                                        type="number"
                                        value={t.number}
                                        onChange={(e) => updateTopField(i, "number", e.target.value)}
                                        className={inputCls}
                                    />
                                </FieldA>
                                <FieldA label="Latitude (decimal)" cols="md:col-span-3">
                                    <input
                                        data-testid={`admin-lat-${i}`}
                                        value={t.custom_lat ?? ""}
                                        onChange={(e) => updateTopField(i, "custom_lat", e.target.value)}
                                        className={inputCls}
                                    />
                                </FieldA>
                                <FieldA label="Longitude (decimal)" cols="md:col-span-3">
                                    <input
                                        data-testid={`admin-lon-${i}`}
                                        value={t.custom_lon ?? ""}
                                        onChange={(e) => updateTopField(i, "custom_lon", e.target.value)}
                                        className={inputCls}
                                    />
                                </FieldA>
                                <FieldA label="Release phase" cols="md:col-span-3">
                                    <select
                                        data-testid={`admin-release-${i}`}
                                        value={t.release_phase}
                                        onChange={(e) => updateTopField(i, "release_phase", e.target.value)}
                                        className={inputCls}
                                    >
                                        {RELEASE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                                    </select>
                                </FieldA>
                                <FieldA label="Manual: open auction?" cols="md:col-span-3">
                                    <label className="flex items-center gap-2 px-3 py-2 bg-slate-950 border border-white/10 cursor-pointer">
                                        <input
                                            data-testid={`admin-isopen-${i}`}
                                            type="checkbox"
                                            checked={!!t.is_open}
                                            disabled={t.release_phase !== "manual"}
                                            onChange={(e) => updateTopField(i, "is_open", e.target.checked)}
                                            className="accent-aurora"
                                        />
                                        <span className="text-xs">{t.is_open ? "OPEN" : "CLOSED"}</span>
                                    </label>
                                </FieldA>
                                <FieldA label="Quote (italic)" cols="md:col-span-12">
                                    <input
                                        data-testid={`admin-quote-${i}`}
                                        value={t.quote || ""}
                                        onChange={(e) => updateTopField(i, "quote", e.target.value)}
                                        placeholder="It's not a place. It's a passage."
                                        className={inputCls}
                                    />
                                </FieldA>
                                <FieldA label="Quote IT" cols="md:col-span-12">
                                    <input
                                        data-testid={`admin-quote-it-${i}`}
                                        value={t.quote_it || ""}
                                        onChange={(e) => updateTopField(i, "quote_it", e.target.value)}
                                        placeholder="Non è un luogo. È un passaggio."
                                        className={inputCls}
                                    />
                                </FieldA>
                                <FieldA label="Description (EN)" cols="md:col-span-12">
                                    <textarea
                                        data-testid={`admin-desc-${i}`}
                                        rows={3}
                                        value={t.description || ""}
                                        onChange={(e) => updateTopField(i, "description", e.target.value)}
                                        className={inputCls + " resize-y"}
                                    />
                                </FieldA>
                                <FieldA label="Description IT" cols="md:col-span-12">
                                    <textarea
                                        data-testid={`admin-desc-it-${i}`}
                                        rows={3}
                                        value={t.description_it || ""}
                                        onChange={(e) => updateTopField(i, "description_it", e.target.value)}
                                        className={inputCls + " resize-y"}
                                    />
                                </FieldA>
                                <FieldA label="Custom image URL (optional · overrides Mapbox satellite)" cols="md:col-span-12">
                                    <input
                                        data-testid={`admin-image-${i}`}
                                        value={t.custom_image_url || ""}
                                        onChange={(e) => updateTopField(i, "custom_image_url", e.target.value)}
                                        placeholder="https://your-cdn.com/portal.png"
                                        className={inputCls}
                                    />
                                    {t.custom_image_url && (
                                        <img
                                            src={t.custom_image_url}
                                            alt="preview"
                                            className="mt-2 max-h-32 border border-white/10"
                                        />
                                    )}
                                </FieldA>
                            </div>
                        </details>
                    ))}
                </div>
                <button
                    data-testid="admin-save"
                    onClick={saveTops}
                    disabled={busy}
                    className="mt-6 btn-sharp bg-white text-obsidian px-6 py-3 text-xs hover:bg-slate-200 disabled:opacity-50"
                >
                    {busy ? "Saving…" : "Save all top tokens"}
                </button>
            </Section>

            {/* REWARD TOKENS */}
            <Section title="Reward tokens" subtitle="Symbolic NFTs with no coordinates — gift them to community members. Optional recipient wallet.">
                <div className="bg-slate-900/40 border border-white/10 p-4 mb-4 grid grid-cols-1 md:grid-cols-12 gap-3">
                    <FieldA label="Title" cols="md:col-span-3">
                        <input
                            data-testid="reward-title"
                            value={newReward.title}
                            onChange={(e) => setNewReward({ ...newReward, title: e.target.value })}
                            placeholder="Founder Badge"
                            className={inputCls}
                        />
                    </FieldA>
                    <FieldA label="Image URL" cols="md:col-span-3">
                        <input
                            data-testid="reward-image"
                            value={newReward.image_url}
                            onChange={(e) => setNewReward({ ...newReward, image_url: e.target.value })}
                            placeholder="https://…"
                            className={inputCls}
                        />
                    </FieldA>
                    <FieldA label="Recipient (optional)" cols="md:col-span-3">
                        <input
                            data-testid="reward-recipient"
                            value={newReward.recipient}
                            onChange={(e) => setNewReward({ ...newReward, recipient: e.target.value })}
                            placeholder="0x…"
                            className={inputCls}
                        />
                    </FieldA>
                    <FieldA label="Description" cols="md:col-span-12">
                        <textarea
                            data-testid="reward-description"
                            rows={2}
                            value={newReward.description}
                            onChange={(e) => setNewReward({ ...newReward, description: e.target.value })}
                            className={inputCls + " resize-y"}
                        />
                    </FieldA>
                    <div className="md:col-span-12">
                        <button
                            data-testid="reward-create"
                            onClick={addReward}
                            className="btn-sharp inline-flex items-center gap-2 bg-aurora text-obsidian px-5 py-3 text-xs hover:bg-amber-400"
                        >
                            <Plus className="w-3 h-3" /> Create reward
                        </button>
                    </div>
                </div>

                {rewards.length === 0 ? (
                    <p className="text-sm text-slate-500 font-mono">No reward tokens yet.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {rewards.map((r) => (
                            <div
                                key={r.id}
                                className="bg-slate-900/40 border border-white/10 p-4 flex flex-col"
                                data-testid={`reward-card-${r.id}`}
                            >
                                {r.image_url && (
                                    <img
                                        src={r.image_url}
                                        alt={r.title}
                                        className="w-full aspect-square object-cover border border-white/10 mb-3"
                                    />
                                )}
                                <div className="font-serif text-xl">{r.title}</div>
                                <div className="text-[11px] font-mono text-slate-400 mt-1 line-clamp-3">
                                    {r.description}
                                </div>
                                {r.recipient && (
                                    <code className="text-[10px] text-frost mt-2 break-all">
                                        → {r.recipient}
                                    </code>
                                )}
                                <button
                                    data-testid={`reward-delete-${r.id}`}
                                    onClick={() => removeReward(r.id)}
                                    className="mt-3 self-start text-xs text-slate-500 hover:text-red-400 inline-flex items-center gap-1"
                                >
                                    <Trash2 className="w-3 h-3" /> Delete
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </Section>

            {/* SOCIALS */}
            <Section title="Social links">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {SOCIAL_DEFS.map(({ key, label, Icon, placeholder }) => (
                        <label
                            key={key}
                            className="flex items-center gap-3 bg-slate-900/40 border border-white/10 px-3 py-2"
                        >
                            <span className="w-9 h-9 flex items-center justify-center border border-white/10 text-frost">
                                <Icon />
                            </span>
                            <div className="flex-1">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
                                <input
                                    data-testid={`admin-social-${key}`}
                                    value={socials[key] || ""}
                                    onChange={(e) => setSocialsState({ ...socials, [key]: e.target.value })}
                                    placeholder={placeholder}
                                    className="w-full bg-transparent border-0 px-0 py-1 text-sm font-mono focus:outline-none"
                                />
                            </div>
                        </label>
                    ))}
                </div>
                <button
                    data-testid="admin-socials-save"
                    onClick={saveSocials}
                    className="mt-4 btn-sharp bg-white text-obsidian px-6 py-3 text-xs hover:bg-slate-200"
                >
                    Save socials
                </button>
            </Section>
        </div>
    );
}

const inputCls =
    "w-full bg-slate-950 border border-white/10 px-3 py-2 text-sm font-mono focus:border-aurora focus:outline-none";

function Section({ title, subtitle, children }) {
    return (
        <section className="mt-16 border-t border-white/10 pt-10">
            <div className="text-xs uppercase tracking-[0.3em] text-frost mb-2">{title}</div>
            {subtitle && <p className="text-sm font-mono text-slate-400 mb-6 max-w-2xl">{subtitle}</p>}
            {children}
        </section>
    );
}

function FieldA({ label, cols, children }) {
    return (
        <label className={`block ${cols}`}>
            <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</span>
            {children}
        </label>
    );
}

function Field2({ label, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</div>
            <div>{children}</div>
        </div>
    );
}
